import { SBRouteResponse } from '~/modules/storyblok/composables/useRoutes/useRoutes';
import { useStoryblokApi } from '@storyblok/nuxt-2';
import { removeLocale } from '~/modules/storyblok/helpers/removeLocale';
import { isExternalLink } from '~/helpers/isExternalLink';
import { normalizeIndexPagePath } from '~/modules/storyblok/helpers/normalizeIndexPagePath';
import { REMOVED_URL_PREFIXES } from '~/modules/storyblok/constants';

interface ResolvedUrlsParams {
  slug: string;
  language?: string;
}

declare module '@nuxt/types' {
  interface Context {
    $localeSbUrl: (params: ResolvedUrlsParams) => string;
    $navigateToPage: (path: string) => void;
  }
}

declare module '@nuxtjs/composition-api' {
  interface Context {
    $localeSbUrl: (params: ResolvedUrlsParams) => string;
    $navigateToPage: (path: string) => void;
  }
}

/**
 * Resolve Slug
 * @param slug
 * @returns
 */

function resolveSlug(slug: string): string {
  let slugCopy = slug;

  const prefix = REMOVED_URL_PREFIXES.find((i) => slug.startsWith(i));
  if (prefix) {
    slugCopy = slugCopy.replace(prefix, '');
  }

  if (slugCopy.endsWith('/')) {
    slugCopy = slugCopy.slice(0, -1);
  }

  return slugCopy;
}

/**
 * Fetch all links from Storyblok
 */
const fetchAllLinks = async () => {
  const storyblokApi = useStoryblokApi();
  const links = [];
  let page = 1;
  let totalLinks = 0;
  const perPage = 1000;

  do {
    // eslint-disable-next-line no-await-in-loop
    const response = await storyblokApi.get('cdn/links', {
      version: process.env.VSF_STORYBLOK_VERSION === 'draft' ? 'draft' : 'published',
      per_page: perPage,
      page,
      resolve_links: 'url',
      cv: Date.now(),
    });
    const { data } = response as { data: SBRouteResponse };
    links.push(...Object.values(data.links));
    totalLinks = Object.values(data.links).length;
    page++;
  } while (totalLinks === perPage);
  return links;
};

export default (context, inject) => {
  const sbPathMap: Record<string, Record<string, string>> = {};
  let linksFetched = false;

  const localeSbUrl = ({ slug, language }: ResolvedUrlsParams): string => {
    if (!slug) {
      return context.localePath('/');
    }

    if (isExternalLink(slug)) {
      return slug;
    }

    const resolvedSlug = resolveSlug(removeLocale(slug));

    const currentLang = language || context.app.i18n.locale;
    const finalSlug = sbPathMap[resolvedSlug]?.[currentLang] || resolvedSlug;

    return context.localePath(`/${normalizeIndexPagePath(finalSlug)}`);
  };

  const navigateToPage = (
    link: SbLink,
  ) => {
    const linkUrl = link.cached_url;
    const isLinkToStory = link.linktype === 'story';

    if (!isLinkToStory && typeof window !== 'undefined' && isExternalLink(linkUrl)) {
      window.open(linkUrl, '_blank');
    } else {
      context.app.router.push(localeSbUrl({ slug: linkUrl }));
    }
  };

  inject('localeSbUrl', localeSbUrl);
  inject('navigateToPage', navigateToPage);

  // eslint-disable-next-line consistent-return
  context.app.router.beforeEach(async (to, from, next) => {
    const currentPath = to.name;
    if (!currentPath) {
      return next();
    }
    const allowedRoutes = ['sb-'];
    const isAllowedRoute = allowedRoutes.some((route) => currentPath.startsWith(route));

    if (isAllowedRoute && !linksFetched) {
      try {
        const links = await fetchAllLinks();
        linksFetched = true;
        links.forEach(({ slug, alternates }: { slug: string; alternates: any }) => {
          if (!alternates || !slug) return;

          const normalizedSlug: string = resolveSlug(slug);

          if (sbPathMap[normalizedSlug]) return;

          sbPathMap[normalizedSlug] = alternates.reduce(
            (acc, { lang, translated_slug }) => ({
              ...acc,
              [lang]: resolveSlug(translated_slug as string) || null,
            }),
            {} as Record<string, string>,
          );
        });
      } catch (err) {
        console.error('Error fetching or processing URLs:', err);
      }
    }
    next();
  });
};
